// frontend/src/pages/storeActivity/StoreHeader.jsx
//
// Who this store is: name, status, plan, and the handful of dates and the
// one address a reader wants before anything else.

import React from 'react';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '../../hooks/useFormatters';
import { relativeTime } from '../../utils/relativeTime';
import { EMPTY, shopPlanReading } from './text';
import StoreIdentity from './StoreIdentity';

export default function StoreHeader({ store, profile, billing, people, lastSeen }) {
  const { t } = useTranslation();
  const { formatCurrency, formatDateTime } = useFormatters();
  const sub = billing.subscription;
  const plan = shopPlanReading(profile, t, formatDateTime);

  // A date with how long ago it was, so "installed 14 Mar 2026" also reads
  // "five months in" without arithmetic.
  const dated = (value) => (value ? `${formatDateTime(value)} · ${relativeTime(value, t)}` : EMPTY);

  return (
    <section className="sa-hero">
      <StoreIdentity store={store}>
        {/* The plan as a chip, priced: it is the one billing fact every
            reader wants, and the rest of billing lives in the details. A
            row with no price (a complimentary tier, a row never read
            back) names the tier alone rather than a dash for money. */}
        <span className="sa-pill sa-pill--plan">
          {planChip(sub, t, formatCurrency)}
        </span>
        <span className={`sa-pill sa-pill--${store.churned ? 'churned' : 'active'}`}>
          {store.churned ? t('storeActivity.picker.churned') : t('storeActivity.picker.active')}
        </span>
        {/* One of our own test stores (tier `internal`, set by an
            operator): never billed, and a number nobody should read as
            a merchant's — the same reason the development case below
            gets a pill. */}
        {sub?.tier === 'internal' && (
          <span className="sa-pill sa-pill--internal">
            {t('storeActivity.billing.states.internal')}
          </span>
        )}
        {/* Only the development case gets a pill. A real merchant is the
            ordinary reading and needs no badge; the plan and the date it
            was observed are stated in full in the Connection panel. */}
        {plan.isDevelopment && (
          <Tooltip title={plan.label} describeChild>
            <span className="sa-pill sa-pill--dev">
              {t('storeActivity.connection.planDevelopment')}
            </span>
          </Tooltip>
        )}
      </StoreIdentity>
      <div className="sa-hero-meta">
        <Meta label={t('storeActivity.lifecycle.organization')} value={store.organization || EMPTY} />
        <Meta label={t('storeActivity.lifecycle.installed')} value={dated(store.installed_at)} />
        {/* The setup popup stands between the install and the app
            (`components/entry/entryScreen.js`); without this date the
            merchant never got past it. */}
        <Meta
          label={t('storeActivity.lifecycle.setupCompleted')}
          value={store.setup_preferences_at
            ? dated(store.setup_preferences_at) : t('storeActivity.lifecycle.notYet')}
        />
        <Meta
          label={t('storeActivity.lifecycle.lastSeen')}
          value={lastSeen ? dated(lastSeen) : t('storeActivity.lifecycle.never')}
        />
        {/* Tracking is younger than most stores; this is where the page's
            history starts, which is not when the merchant's did. */}
        <Meta label={t('storeActivity.lifecycle.firstEvent')} value={dated(store.first_event)} />
        <Meta label={t('storeActivity.lifecycle.contact')} value={people.contact_email || EMPTY} />
        {store.uninstalled_at && (
          <Meta label={t('storeActivity.lifecycle.uninstalled')} value={dated(store.uninstalled_at)} churn />
        )}
      </div>
    </section>
  );
}

function planChip(sub, t, formatCurrency) {
  if (!sub) return t('storeActivity.glance.noPlan');
  if (sub.price_usd === null || sub.price_usd === undefined) return sub.tier;
  return t('storeActivity.glance.planPrice', {
    tier: sub.tier, price: formatCurrency(sub.price_usd, 'USD'),
  });
}

function Meta({ label, value, churn }) {
  return (
    <div className="sa-meta-item">
      <span className="sa-meta-label">{label}</span>
      <span className={`sa-meta-value${churn ? ' sa-meta-value--churn' : ''}`}>{value}</span>
    </div>
  );
}
